import "./souvenir.ts";
import "../souvenir/tokens.css";
// After Souvenir's faces: the rest of their Latin, tried first for the letters it adds (web/fonts/README.md).
import "../fonts/fonts.css";
import "../souvenir/bundle.css";
import "./app.css";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./App.tsx";
import { api } from "./api.ts";
import { applyAppearance, savedAppearance } from "./appearance.ts";
import { CaughtApp } from "./crash.tsx";
import { joinKey, startEarly, tripKey } from "./early.ts";
import { FeedbackHost } from "./Feedback.tsx";
import { loadMap } from "./map/load.tsx";
import { listenForInstall, registerWorker } from "./offline.ts";
import { takeOver } from "./opening.tsx";
import { RedeemHost } from "./Redeem.tsx";
import { opensOnMap } from "./reopen.ts";
import { parse, useRoute } from "./router.ts";
import { ViewPage, viewKey } from "./ViewPage.tsx";
import { startViewing } from "./viewing.ts";

// The sunrise the page drew is the app's now (features/opening.md), and what the page opened waits for
// starts loading at once: who I am, and alongside it (not after it) the trip, my trips or the join link's.
takeOver();
const opened = parse();
// A shared trip's view link (features/sharing.md) is its own page: it never asks who I am (it opens signed out), and
// everything it addresses is the link's (viewing.ts).
if (opened.name === "view") {
  startViewing(opened.code);
  startEarly(viewKey(opened.code), () => api.view(opened.code));
  if (matchMedia("(min-width: 900px)").matches || !opened.view) loadMap().catch(() => {});
} else startEarly("me", api.me);
if (opened.name === "trip") {
  const trip = opened.trip;
  startEarly(tripKey(trip), () => Promise.all([api.trip(trip), api.items(trip)]));
  // The map's code, alongside them when the trip opens on its map; a page without one never waits for it
  // (map/load.tsx). Not loading says so on the map itself.
  if (opensOnMap(trip, matchMedia("(min-width: 900px)").matches)) loadMap().catch(() => {});
} else if (opened.name === "trips") startEarly("trips", api.trips);
else if (opened.name === "join") startEarly(joinKey(opened.code), () => api.join(opened.code));
// The browser bars follow the chosen theme too (index.html already set the page).
applyAppearance(savedAppearance());
// Ready without signal: the worker keeps the app and what I open on this phone.
registerWorker();
listenForInstall();
// A page that breaks while it's drawn says so, never a blank page, and what broke goes to the console once,
// with what it was (crash.tsx), rather than also as React's own. The feedback form is beside it, not in it, so
// Report this on a page that broke still opens it; so is the sheet that redeems a code (Redeem.tsx).
createRoot(document.getElementById("root") as HTMLElement, { onCaughtError: () => {} }).render(
  <StrictMode>
    <FeedbackHost />
    <RedeemHost />
    <CaughtApp>{opened.name === "view" ? <ViewApp /> : <App />}</CaughtApp>
  </StrictMode>,
);

// The view link's page, at whichever of its steps the address is.
function ViewApp() {
  const route = useRoute();
  return route.name === "view" ? <ViewPage key={route.code} route={route} /> : null;
}
